var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { setupIntl } from '@jetshop/intl/config';
import * as Sentry from '../../sentry/client';
import React from 'react';
import { Cookies } from 'react-cookie';
import { hydrate as renderToDOM } from 'react-dom';
import { loadableReady } from '@loadable/component';
import { BrowserRouter } from 'react-router-dom';
import { ChannelHandler } from '../../ChannelHandler/ChannelHandler';
import { getSelectedAndRecommendedChannel } from '../../ChannelHandler/channelUtils';
import { CartIdProvider } from '../../components/Cart/CartIdContext';
import logWarningInDev from '../../helpers/logWarningInDev';
import { getActiveFiltersFromSearch } from '../../resolvers/filters/filterParams';
import { createClient } from '../apollo';
import { AuthHandler } from '../AuthHandler';
import channelsQuery from '../ChannelsQuery.gql';
import { PreviewHandler } from '../PreviewHandler';
import SharedTree from '../SharedTree';
import createIntlOptions from '../utils/createIntOptions';
import { setUID } from '../utils/uid';
import { webVitals } from '../../analytics/webVitals';
import { primaryRouteForPathFetcher } from '../../../core/ChannelHandler/redirectUtils';
// Instantiate this here, since we need to read cookies outside of React.
const cookies = new Cookies();
function getInitialCacheData() {
    return {
        data: {
            activeFilters: getActiveFiltersFromSearch(document.location.search)
        }
    };
}
const startClient = (tree, config) => {
    if (!config.apolloConfig.channelsQuery) {
        logWarningInDev(true, `No channelsQuery was provided in the apolloConfig, which is a
      deprecated behaviour. Please update your shop.config.js to include the
      ChannelsQuery to avoid breaking in the next major release. See
      https://gitlab.jetshop.se/jetshop/flight/blob/master/packages/template-trend/src/ChannelsQuery.gql
      for a reference implementation`);
    }
    // This is used by cypress to get the shopid when running universal cypress test
    window.shopid = config.apolloConfig.shopid;
    if (process.env.REACT_APP_REPORT_WEB_VITALS) {
        webVitals({
            shopId: config.apolloConfig.shopid,
            apiUrl: config.apolloConfig.graphQLURI
        });
    }
    if (config.sentry && config.sentry.clientDSN) {
        Sentry.init(config.sentry.clientDSN);
    }
    const { apolloConfig } = config;
    const authHandler = new AuthHandler({
        cookies
    });
    const previewHandler = new PreviewHandler();
    const channelHandler = new ChannelHandler();
    const { client } = createClient({
        apolloConfig,
        authHandler,
        previewHandler,
        channelHandler
    });
    channelHandler.setGetPrimaryRoutePath(primaryRouteForPathFetcher(client));
    const initialCacheData = getInitialCacheData();
    // Initialise cache with active filters from the search params
    client.cache.writeData(initialCacheData);
    // If cache is reseted, we reset the initial cache data
    client.onResetStore(() => __awaiter(void 0, void 0, void 0, function* () { return client.cache.writeData(initialCacheData); }));
    client
        .query({
        query: config.apolloConfig.channelsQuery || channelsQuery
    })
        .then(({ data }) => {
        const channels = data.channels;
        const geoipCountry = window.__GEOIP_COUNTRY__;
        const acceptedLanguages = JSON.parse(window.__ACCEPTED_LANGUAGES__);
        const { selectedChannel, recommendedChannel, group } = getSelectedAndRecommendedChannel({
            url: window.location.href,
            cookies,
            channels,
            singleDomainMode: config.singleDomainMode,
            geoipCountry,
            acceptedLanguages
        });
        channelHandler.setSelectedChannel(selectedChannel);
        channelHandler.setRecommendedChannel(recommendedChannel);
        channelHandler.setChannels(channels);
        channelHandler.setChannelGroup(group);
        const channelOverrides = config.channelOverrides && selectedChannel.id
            ? config.channelOverrides[selectedChannel.id]
            : {};
        const { intl } = Object.assign(Object.assign({}, config), channelOverrides);
        const intlOptions = createIntlOptions({
            selectedChannel,
            options: intl.options
        });
        return setupIntl({
            translations: intl.translations,
            locale: selectedChannel.language.culture,
            defaultLocale: intl.defaultLocale,
            options: intlOptions
        }).then(() => {
            const activeBreakpoint = window.__ACTIVE_BREAKPOINT__;
            const releware = {
                enabled: config.relewareEnabled,
                baseUrl: document.domain,
                uid: config.relewareEnabled ? setUID(cookies) : null
            };
            // Disable client network fetch during SSR, and re-enable it after
            // hydration. This prevents `cache-and-network` routes from re-fetching
            // immediately after hydration
            client.disableNetworkFetches = true;
            const wrappedTree = (React.createElement(SharedTree, { activeBreakpoint: activeBreakpoint, config: Object.assign(Object.assign({}, config), channelOverrides), cookies: cookies, releware: releware, client: client, Router: BrowserRouter, authHandler: authHandler, channelHandler: channelHandler, previewHandler: previewHandler },
                React.createElement(CartIdProvider, null, tree)));
            return wrappedTree;
        });
    })
        .then((tree) => {
        return loadableReady(() => {
            const root = document.getElementById('root');
            renderToDOM(tree, root, () => void (client.disableNetworkFetches = false));
        });
    });
};
const main = (tree, config) => {
    // If the polyFillsLoaded flag is set to true then we know that the polyFills have been loaded and we can start the client.
    if (window.polyFillsLoaded) {
        return startClient(tree, config);
    }
    else {
        // If the polyfills have not been loaded we want to replace the bootShop to start the client instead so that once the polyfills are loaded, bootShop will start the client.
        window.bootShop = () => startClient(tree, config);
    }
};
export default main;
//# sourceMappingURL=startClient.js.map